import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { SelectorTriggerProps } from "@/v2-backoffice-client/components/selector"
import Button from "@/v2-backoffice-client/components/button"
import Form from "@/v2-backoffice-client/components/form"
import { useTenantQuery } from "@/v2-backoffice-client/tenant/tenant.query"
import TenantCreateDialog from "@/v2-backoffice-client/tenant/create/TenantCreateDialog"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"
import tenantStub from "@/v2-backoffice-client/tenant/tenant.stub"
import { IS_DELETED_COLUMN } from "@/v2-backoffice-client/tenant/tenant.constants"

export type TenantPickerProps = Omit<
  SelectorTriggerProps,
  "onSelect" | "value" | "name"
> & {
  enableCreate?: boolean
  defaultExternalCustomerId?: string
  /** Pre-seeds the search — e.g. a team name pasted from a spreadsheet,
   *  so the match (or the create flow's name) is ready on open. */
  defaultSearch?: string
  onSelect(tenant: TenantFragment): void
  // Selected tenant id (or any truthy marker that one was picked). When
  // `required` is set, this drives the hidden validity-bearing input that
  // makes the picker participate in `Form` validation.
  value?: number | string | null
  required?: boolean
  name?: string
}

function TenantPicker(props: TenantPickerProps) {
  const {
    onSelect,
    enableCreate,
    defaultExternalCustomerId,
    defaultSearch,
    value,
    required,
    name,
    ...triggerProps
  } = props

  const [search, setSearch] = useState(defaultSearch ?? "")
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const { data, loading, error } = useTenantQuery({
    skip: search.length < 2,
    variables: {
      input: {
        limit: 10,
        // Active teams only: every picker consumer is choosing a team to
        // put people or invitations INTO, and a soft-deleted team is never
        // that. Offering one bricked dev user-creation on a dead team
        // (found live 2 Sep) — removed teams are restored from the teams
        // board, not picked here.
        filters:
          search.length >= 2
            ? [
                { column: "Name", contains: search },
                { column: IS_DELETED_COLUMN, eq: "0" },
              ]
            : undefined,
      },
    },
  })

  errorToast(error, {
    title: "Failed to query for teams",
  })

  const tenants = data?.getTenants.items ?? []

  return (
    <div className="tenant-picker form-required-host">
      <Selector>
        <Selector.Trigger iconSrc="search" {...triggerProps}>
          {triggerProps.children ?? "Search teams..."}
        </Selector.Trigger>

        <Selector.Content>
          <Selector.Search
            placeholder="Search by team name..."
            onSearch={setSearch}
            defaultValue={defaultSearch}
          />

          <Selector.List
            emptyLabel="No teams found"
            isLoading={loading}
            error={error}
            noResults={tenants.length === 0}
          >
            {tenants.map((tenant) => (
              <Selector.Item
                key={tenant.Id}
                value={String(tenant.Id)}
                onSelect={() => onSelect(tenant)}
              >
                <Selector.ItemText
                  primary={tenant.Name}
                  secondary={`ID: ${tenant.Id}`}
                />
              </Selector.Item>
            ))}
          </Selector.List>

          {enableCreate && (
            <Selector.Footer>
              <Button
                size="xs"
                variant="ghost"
                iconSrc="plus"
                onClick={() => {
                  setIsCreateOpen(true)
                }}
              >
                Create new team
              </Button>
            </Selector.Footer>
          )}
        </Selector.Content>
      </Selector>

      {required && (
        <Form.RequiredSlot
          name={name}
          required
          value={value ?? ""}
          aria-label={triggerProps["aria-label"]}
        />
      )}

      {enableCreate && (
        <TenantCreateDialog
          open={isCreateOpen}
          onOpenChange={setIsCreateOpen}
          defaultValues={{
            // Seed the create form with what the operator was searching
            // for: no match + "Create new team" means the search IS the
            // intended name.
            Name: search,
            ExternalCustomerId: defaultExternalCustomerId ?? "",
          }}
          onCreated={(tenant) => {
            onSelect({
              ...tenantStub,
              ...tenant,
            })
          }}
        />
      )}
    </div>
  )
}

export default TenantPicker
